import React, { useEffect, useState } from 'react';
import io from 'socket.io-client';
import Grid from './components/Grid';
import ModernEditor from './components/ModernEditor';
import { 
  Container, 
  Title, 
  Text, 
  Card, 
  Flex, 
  Badge, 
  ProgressBar,
  Button
} from './components/UIComponents';
import theme from './styles/theme';
import GameStats from './components/GameStats';
import { MobileProvider } from './contexts/MobileContext';
import config from './config';

// Initialize socket but don't connect immediately
const socket = io(config.SOCKET_URL, config.SOCKET_OPTIONS);

// Helper function to get cell color based on player ID
const getCellColor = (playerId) => {
  const colors = [
    '#000000', // Empty cell
    '#FF4136', // Player 1 - Red
    '#2ECC40', // Player 2 - Green
    '#0074D9', // Player 3 - Blue
    '#FFDC00', // Player 4 - Yellow
    '#B10DC9', // Player 5 - Purple
    '#FF851B', // Player 6 - Orange
    '#7FDBFF', // Player 7 - Light Blue
    '#01FF70'  // Player 8 - Lime
  ];
  return colors[playerId] || colors[0];
};

// CSS for entire app
const globalStyle = `
  body {
    margin: 0;
    padding: 0;
    background-color: ${theme.colors.background};
    color: ${theme.colors.textPrimary};
    font-family: ${theme.typography.fontFamily};
    min-height: 100vh;
    overflow-x: hidden;
  }

  * {
    box-sizing: border-box;
  }

  @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap');
`;

// Add these button style objects near the top where other styles are defined
const buttonBaseStyle = {
  padding: '12px 20px',
  fontSize: '1rem',
  fontWeight: '600',
  border: 'none',
  borderRadius: theme.borders.radius.md,
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  width: '100%',
  textAlign: 'center',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
  },
  '&:active': {
    transform: 'translateY(1px)'
  }
};

const justPlayButtonStyle = {
  ...buttonBaseStyle,
  background: `linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.accent})`,
  color: 'white',
  marginBottom: '12px',
  fontSize: '1.1rem',
  padding: '14px 24px',
  boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
};

const createRoomButtonStyle = {
  ...buttonBaseStyle,
  background: `linear-gradient(135deg, ${theme.colors.secondary}, ${theme.colors.primary})`,
  color: 'white',
  marginBottom: '12px',
};

const joinRoomButtonStyle = {
  ...buttonBaseStyle,
  background: `linear-gradient(135deg, ${theme.colors.accent}, ${theme.colors.secondary})`,
  color: 'white',
};

const backButtonStyle = {
  ...buttonBaseStyle,
  backgroundColor: theme.colors.background,
  border: `2px solid ${theme.colors.border}`,
  color: theme.colors.textPrimary,
  marginTop: '8px',
};

const inputStyle = {
  width: '100%',
  padding: '12px 16px',
  fontSize: '1rem',
  borderRadius: theme.borders.radius.md,
  border: `2px solid ${theme.colors.border}`,
  backgroundColor: theme.colors.background,
  color: theme.colors.textPrimary,
  marginBottom: '16px',
  transition: 'all 0.3s ease',
  '&:focus': {
    outline: 'none',
    borderColor: theme.colors.primary,
    boxShadow: `0 0 0 3px ${theme.colors.primary}33`
  }
};

// Add this style near the top of the file
const floatingLaunchPanelStyle = {
  position: 'fixed',
  zIndex: 1000,
  bottom: '20px',
  right: '20px',
  minWidth: '200px',
  backgroundColor: theme.colors.card,
  borderRadius: theme.borders.radius.lg,
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
  border: `1px solid ${theme.colors.border}`,
  padding: theme.spacing.md
};

function App() {
  const [isConnected, setIsConnected] = useState(false);
  const [username, setUsername] = useState('');
  const [usernameError, setUsernameError] = useState(null);
  const [playerId, setPlayerId] = useState(null);
  const [numericId, setNumericId] = useState(null);
  const [gameState, setGameState] = useState(null);
  const [error, setError] = useState(null);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);
  const [lastPatternTime, setLastPatternTime] = useState(0);
  const [pattern, setPattern] = useState(Array(5).fill().map(() => Array(5).fill(0)));
  const [playerCount, setPlayerCount] = useState(0);
  const [gameStarted, setGameStarted] = useState(false);
  const [waitingForPatterns, setWaitingForPatterns] = useState(false);
  const [hasSubmittedPattern, setHasSubmittedPattern] = useState(false);
  const [earthquake, setEarthquake] = useState(null);
  const [earthquakeCooldown, setEarthquakeCooldown] = useState(0);
  const [roomId, setRoomId] = useState('');
  const [showJoinRoom, setShowJoinRoom] = useState(false);
  const [gameStats, setGameStats] = useState({ totalRooms: 0, totalPlayers: 0 });
  const [gameOver, setGameOver] = useState(false);
  const [winner, setWinner] = useState(null);
  
  // To find the highest score for the progress bar
  const getMaxScore = () => {
    if (!gameState || !gameState.players) return config.WINNING_POINTS;
    
    const scores = Object.values(gameState.players).map(p => p.points);
    return Math.max(config.WINNING_POINTS, ...scores);
  };

  // Add a debug effect to monitor pattern changes
  useEffect(() => {
    console.log("Pattern state changed:", pattern);
  }, [pattern]);

  // Connect socket when component mounts to receive stats
  useEffect(() => {
    // Only connect if not already connected
    if (!socket.connected) {
      socket.connect();
    }

    socket.on('connect', () => {
      console.log('Connected to server for stats');
    });

    socket.on('gameStats', (stats) => {
      console.log('Received game stats:', stats);
      setGameStats(stats);
    });

    socket.on('disconnect', () => {
      console.log('Disconnected from server');
      setIsConnected(false);
      setGameState(null);
    });

    return () => {
      socket.off('gameStats');
      socket.off('connect');
      socket.off('disconnect');
    };
  }, []);

  // Separate effect for game-specific socket events
  useEffect(() => {
    socket.on('gameState', (state) => {
      console.log('Game state received:', state);
      setGameState(state);
      setNumericId(state.players[socket.id]?.id || null);
      if (state.earthquake) {
        setEarthquake(state.earthquake);
      } else {
        setEarthquake(null);
      }
      setIsConnected(true);
      setPlayerId(socket.id);
    });

    socket.on('playerCount', (count) => setPlayerCount(count));
    socket.on('gameStarted', (started) => setGameStarted(started));
    socket.on('waitingForPatterns', (waiting) => setWaitingForPatterns(waiting));
    socket.on('patternError', (message) => setError(message));
    socket.on('usernameError', (message) => {
      setUsernameError(message);
      // If the error is about a terminated room, reset the room ID
      if (message.includes('game has ended')) {
        setRoomId('');
        setShowJoinRoom(false);
      }
    });
    socket.on('earthquake', (position) => {
      console.log('Earthquake at position:', position);
      setEarthquake(position);
      
      // Clear earthquake effect after animation
      setTimeout(() => {
        setEarthquake(null);
      }, 2000);
    });

    socket.on('gameOver', ({ winner }) => {
      setGameOver(true);
      setWinner(winner);
      setGameStarted(false);
      // When game is over, clear the room ID since it's now terminated
      setRoomId('');
    });

    return () => {
      socket.off('gameState');
      socket.off('playerCount');
      socket.off('gameStarted');
      socket.off('waitingForPatterns');
      socket.off('patternError');
      socket.off('usernameError');
      socket.off('earthquake');
      socket.off('gameOver');
    };
  }, []);

  const handleJoinGame = () => {
    if (!username.trim()) {
      setUsernameError('Please enter a username');
      return;
    }

    setUsernameError(null);
    socket.emit('setUsername', { 
      username: username.trim(),
      roomId: roomId.trim() || null
    });
    setShowJoinRoom(false);
  };

  const handleCreateRoom = () => {
    if (!username.trim()) {
      setUsernameError('Please enter a username');
      return;
    }

    setUsernameError(null);
    socket.emit('setUsername', { 
      username: username.trim(),
      roomId: null,
      createNew: true
    });
    setShowJoinRoom(false);
  };

  const handleJustPlay = () => {
    if (!username.trim()) {
      setUsernameError('Please enter a username');
      return;
    }
    setUsernameError(null);
    socket.emit('setUsername', { 
      username: username.trim(),
      roomId: null, // This will trigger random room selection on the server
      createNew: false
    });
  };

  // Combined function to handle grid cell click and submit pattern if applicable
  const handleGridCellClick = (i, j) => {
    console.log("Grid clicked at", i, j);
    console.log("Current pattern:", pattern);
    
    // Always update the selected position regardless of other conditions
    setSelectedPosition({ x: i, y: j });
    
    // Check if there is a valid pattern (at least one live cell)
    const hasValidPattern = pattern && pattern.some(row => row.some(cell => cell === 1));
    console.log("Has valid pattern:", hasValidPattern);
    
    // If there's an active cooldown, show the error and clear highlight after brief delay
    if (cooldownRemaining > 0) {
      // No need to set error text for cooldown anymore - the overlay handles this
      
      // Clear highlight after brief feedback even during cooldown
      setTimeout(() => {
        setSelectedPosition(null);
      }, 800);
      
      return;
    }
    
    // If there's no valid pattern, show an error and clear highlight after brief delay
    if (!hasValidPattern) {
      setError('Pattern is empty. Please design a pattern first.');
      
      // Clear highlight after brief feedback
      setTimeout(() => {
        setSelectedPosition(null);
      }, 800);
      
      return;
    }
    
    // If we get here, proceed with placing the pattern
    const now = Date.now();
    if (now - lastPatternTime < 5000) {
      // No need to set error text for cooldown anymore - the overlay handles this
      return;
    }
    
    // Submit the pattern
    setError(null);
    console.log('Submitting pattern at position:', { x: i, y: j });
    console.log('Pattern being submitted:', pattern);
    
    // Create a payload to log
    const payload = { pattern, position: { x: i, y: j } };
    console.log('Full payload:', payload);
    
    socket.emit('submitPattern', payload, (response) => {
      console.log('Server response to pattern submission:', response);
      if (response.success) {
        setLastPatternTime(now);
        setCooldownRemaining(5);
        setError(null);
        
        // Clear the selected position to hide the highlight
        setTimeout(() => {
          setSelectedPosition(null);
        }, 800); // Small delay to show a brief visual feedback
        
        // Start cooldown timer
        const timer = setInterval(() => {
          setCooldownRemaining(prev => {
            if (prev <= 1) {
              clearInterval(timer);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else if (response.cooldownRemaining > 0) {
        // No need to set error text for cooldown anymore - the overlay handles this
      } else {
        setError('Failed to launch pattern. Please try again.');
      }
    });
  };

  // Keep the original handleSubmitPattern for potential use from the editor panel
  const handleSubmitPattern = () => {
    if (!pattern || !pattern.some(row => row.some(cell => cell))) {
      setError('Pattern is empty. Please design a pattern first.');
      return;
    }

    if (!selectedPosition) {
      setError('Please select a position on the grid to launch the pattern.');
      return;
    }

    const now = Date.now();
    if (now - lastPatternTime < 5000) {
      // No need to set error text for cooldown anymore - the overlay handles this
      return;
    }

    setError(null);
    console.log('Submitting pattern:', pattern);
    socket.emit('submitPattern', { 
      pattern,
      position: selectedPosition
    }, (response) => {
      if (response.success) {
        setLastPatternTime(now);
        setCooldownRemaining(5);
        setError(null);
        // Clear the selected position after brief visual feedback
        setTimeout(() => {
          setSelectedPosition(null);
        }, 800);
        
        // Start cooldown timer
        const timer = setInterval(() => {
          setCooldownRemaining(prev => {
            if (prev <= 1) {
              clearInterval(timer);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else if (response.cooldownRemaining > 0) {
        // No need to set error text for cooldown anymore - the overlay handles this
      } else {
        setError('Failed to launch pattern. Please try again.');
      }
    });
  };

  const getPlayerInfo = () => {
    if (!gameState || !gameState.players) return [];
    
    return Object.entries(gameState.players).map(([id, data]) => ({
      id,
      numericId: data.id,
      username: data.username,
      points: data.points,
      isCurrentPlayer: id === playerId,
      color: getCellColor(data.id)
    }));
  };

  // Add global styles
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.type = 'text/css';
    styleElement.appendChild(document.createTextNode(globalStyle));
    document.head.appendChild(styleElement);
    
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  // Add this style for the game container
  const gameContainerStyle = {
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100vh - 48px)', // Full height minus some padding
    maxWidth: '100%'
  };

  // Style for the game area to take most of the space
  const gameAreaStyle = {
    flex: '1',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0, // Important for flex child to be able to shrink
    overflow: 'hidden' // Prevent scrolling inside the card
  };

  // Style for the bottom panels to take minimum required space
  const bottomPanelStyle = {
    flexShrink: 0
  };

  // Add a handler for the earthquake trigger button
  const handleTriggerEarthquake = () => {
    // Helper function to start cooldown timer
    const startCooldownTimer = (duration) => {
      setEarthquakeCooldown(duration);
      
      const timer = setInterval(() => {
        setEarthquakeCooldown(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    };
    
    socket.emit('triggerEarthquake', {}, (response) => {
      if (response.success) {
        // Set cooldown on success
        startCooldownTimer(config.EARTHQUAKE_COOLDOWN); // 30 seconds cooldown
      } else {
        setError(response.error || 'Failed to trigger earthquake');
        
        if (response.cooldownRemaining > 0) {
          startCooldownTimer(response.cooldownRemaining);
        }
      }
    });
  };

  const handlePlayAgain = () => {
    // Disconnect from current room
    socket.emit('leaveRoom');
    socket.disconnect();

    // Reset game states
    setGameOver(false);
    setWinner(null);
    setGameState(null);
    setIsConnected(false);
    setPlayerId(null);
    setNumericId(null);
    setSelectedPosition(null);
    setCooldownRemaining(0);
    setLastPatternTime(0);
    setPattern(Array(5).fill().map(() => Array(5).fill(0)));
    setPlayerCount(0);
    setGameStarted(false);
    setWaitingForPatterns(false);
    setHasSubmittedPattern(false);
    setEarthquake(null);
    setEarthquakeCooldown(0);
    setRoomId('');
    setShowJoinRoom(false);

    // Reconnect socket for new game
    socket.connect();
  };

  if (!isConnected) {
    return (
      <Container>
        <Card style={{ 
          maxWidth: '400px', 
          margin: '100px auto',
          padding: '32px',
          backgroundColor: theme.colors.card,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
          border: `1px solid ${theme.colors.border}`
        }}>
          <Title style={{ 
            textAlign: 'center', 
            marginBottom: '24px',
            fontSize: '2rem',
            background: `linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.accent})`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            Pattern Wars
          </Title>
          
          {/* Game Description */}
          <Text style={{
            textAlign: 'center',
            marginBottom: '20px',
            color: theme.colors.textPrimary,
            fontSize: '0.9rem',
            lineHeight: '1.6',
            padding: '16px',
            backgroundColor: `${theme.colors.background}88`,
            borderRadius: theme.borders.radius.lg,
            border: `1px solid ${theme.colors.border}44`,
            boxShadow: `0 4px 12px ${theme.colors.border}22`,
            backdropFilter: 'blur(8px)',
            maxWidth: '360px',
            margin: '0 auto 20px',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '2px',
              background: `linear-gradient(90deg, ${theme.colors.primary}, ${theme.colors.accent})`,
              borderRadius: `${theme.borders.radius.lg} ${theme.borders.radius.lg} 0 0`
            }
          }}>
            A multiplayer Conway's Game of Life battle! Design patterns, expand territory, and use earthquakes to disrupt opponents. First to {config.WINNING_POINTS} points wins!
          </Text>

          <Text style={{
            textAlign: 'center',
            marginBottom: '24px',
            color: theme.colors.textSecondary,
            fontSize: '0.9rem'
          }}>
            {gameStats.totalPlayers} player{gameStats.totalPlayers !== 1 ? 's' : ''} online • {gameStats.totalRooms} active room{gameStats.totalRooms !== 1 ? 's' : ''}
          </Text>
          
          <Flex direction="column" gap="md">
            <input
              type="text"
              placeholder="Enter your username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              style={inputStyle}
            />
            {showJoinRoom ? (
              <>
                <input
                  type="text"
                  placeholder="Enter room ID"
                  value={roomId}
                  onChange={(e) => setRoomId(e.target.value.toUpperCase())}
                  style={inputStyle}
                />
                <Button 
                  onClick={handleJoinGame}
                  style={createRoomButtonStyle}
                >
                  Join Room
                </Button>
                <Button 
                  onClick={() => setShowJoinRoom(false)}
                  style={backButtonStyle}
                >
                  Back
                </Button>
              </>
            ) : (
              <>
                <Button 
                  onClick={handleJustPlay}
                  style={justPlayButtonStyle}
                >
                  Just Play
                </Button>
                <Button 
                  onClick={handleCreateRoom}
                  style={createRoomButtonStyle}
                >
                  Create New Room
                </Button>
                <Button 
                  onClick={() => setShowJoinRoom(true)}
                  style={joinRoomButtonStyle}
                >
                  Join with Room ID
                </Button>
              </>
            )}
            {usernameError && (
              <Text style={{ 
                color: theme.colors.error, 
                marginTop: '12px',
                textAlign: 'center',
                padding: '8px',
                backgroundColor: `${theme.colors.error}11`,
                borderRadius: theme.borders.radius.sm,
                fontSize: '0.9rem'
              }}>
                {usernameError}
              </Text>
            )}
          </Flex>
        </Card>
      </Container>
    );
  }

  return (
    <Container>
      <Flex 
        align="center" 
        justify="center" 
        direction="column" 
        style={{ minHeight: '100vh', position: 'relative' }}
      >
        <MobileProvider>
          <Flex direction="column" gap="md" style={gameContainerStyle}>
            {/* Game area - Takes most of the space */}
            <Card padding="medium" elevation="large" style={{...gameAreaStyle, marginBottom: theme.spacing.md}}>
              {gameState?.grid && (
                <Grid 
                  grid={gameState.grid} 
                  onCellClick={handleGridCellClick}
                  selectedPosition={selectedPosition}
                  earthquake={earthquake}
                  pattern={pattern}
                />
              )}
            </Card>
            
            {/* Pattern Editor and Game Stats are now managed by MobileContext and positioned properly */}
            <ModernEditor
              pattern={pattern}
              setPattern={setPattern}
              playerColor={getCellColor(numericId)}
              onSubmitPattern={handleSubmitPattern}
              onTriggerEarthquake={handleTriggerEarthquake}
              error={error}
              cooldownRemaining={cooldownRemaining}
              earthquakeCooldown={earthquakeCooldown}
            />

            {/* Game Stats */}
            {gameState?.players && (
              <GameStats 
                players={gameState.players}
                playerCount={playerCount}
                roomId={gameState.roomId}
              />
            )}

            {gameOver && winner && (
              <Card style={{
                backgroundColor: theme.colors.success,
                padding: theme.spacing.lg,
                textAlign: 'center',
                position: 'fixed',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 1000,
                boxShadow: '0 0 20px rgba(0,0,0,0.5)'
              }}>
                <Title style={{ color: '#fff', marginBottom: theme.spacing.md }}>
                  Game Over!
                </Title>
                <Text style={{ color: '#fff', fontSize: '1.2rem', marginBottom: theme.spacing.lg }}>
                  {winner.username} wins with {winner.points} points!
                </Text>
                <Button
                  onClick={handlePlayAgain}
                  style={{
                    backgroundColor: '#fff',
                    color: theme.colors.success,
                    padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
                    border: 'none',
                    borderRadius: theme.borders.radius.md,
                    fontSize: '1.1rem',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    transition: 'transform 0.2s ease',
                    '&:hover': {
                      transform: 'scale(1.05)'
                    }
                  }}
                >
                  Play Again
                </Button>
              </Card>
            )}
          </Flex>
        </MobileProvider>
      </Flex>
    </Container>
  );
}

export default App;